import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import IndiaOpsTab from '../invoice/IndiaOpsTab';

interface GstBooking {
  booking_id: number;
  itinerary_id: string;
  guest_name: string;
  agency_name: string;
  travel_date: string;
  gst_invoice_number: string;
  gst_status: string;
  total_gst_invoice_inr: number;
  collected_inr: number;
  collection_balance_inr: number;
  package_cost_usd: number;
  remitted_usd: number;
  remittance_balance_usd: number;
  handling_fee_inr: number;
  gst_collected: number;
  gst_remitted: number;
  gst_outstanding: number;
  customer_pan: string;
}

const IndiaOpsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<GstBooking[]>([]);
  const [totals, setTotals] = useState({ totalCollectionPending: 0, totalRemittancePending: 0, totalGstOutstanding: 0, totalHandlingFees: 0 });
  const [filters, setFilters] = useState({ travelDateFrom: '', travelDateTo: '', status: '', agencyName: '' });
  const [selectedBooking, setSelectedBooking] = useState<GstBooking | null>(null);
  const [showBatchRemit, setShowBatchRemit] = useState(false);
  const [selectedForRemit, setSelectedForRemit] = useState<Set<number>>(new Set());
  const [batchRemitDate, setBatchRemitDate] = useState(new Date().toISOString().split('T')[0]);
  const [batchRemitRef, setBatchRemitRef] = useState('');
  const [batchRemitRate, setBatchRemitRate] = useState(0);
  const [savingBatch, setSavingBatch] = useState(false);
  const [viewMode, setViewMode] = useState<'bookings' | 'remittances'>('bookings');
  const [remittances, setRemittances] = useState<any[]>([]);
  const [remittanceTotals, setRemittanceTotals] = useState({ total_usd: 0, total_inr: 0, count: 0 });

  useEffect(() => { loadData(); loadRemittances(); }, []);

  const getToken = () => sessionStorage.getItem('jwt_token');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      if (filters.status) params.append('status', filters.status);
      if (filters.agencyName) params.append('agency_name', filters.agencyName);
      const res = await fetch(`/api/india-ops/dashboard?${params}`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setBookings(data.data); setTotals(data.totals || totals); }
    } catch (e) { console.error('Error loading:', e); }
    finally { setLoading(false); }
  };

  const loadRemittances = async () => {
    try {
      const res = await fetch(`/api/india-ops/remittances/all`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) { setRemittances(data.data); setRemittanceTotals(data.totals || { total_usd: 0, total_inr: 0, count: 0 }); }
    } catch (e) { console.error('Error loading remittances:', e); }
  };

  const toggleRemitSelection = (id: number) => {
    setSelectedForRemit(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const selectAllPendingRemit = () => {
    setSelectedForRemit(new Set(bookings.filter(b => b.remittance_balance_usd > 0).map(b => b.booking_id)));
  };

  const batchRemitTotal = bookings.filter(b => selectedForRemit.has(b.booking_id)).reduce((s, b) => s + b.remittance_balance_usd, 0);
  const batchRemitInr = batchRemitRate > 0 ? batchRemitTotal * batchRemitRate : 0;

  const submitBatchRemittance = async () => {
    if (selectedForRemit.size === 0 || !batchRemitDate || !batchRemitRef || batchRemitRate <= 0) { alert('Fill all fields and select bookings'); return; }
    setSavingBatch(true);
    try {
      const promises = Array.from(selectedForRemit).map(bookingId => {
        const b = bookings.find(x => x.booking_id === bookingId);
        if (!b || b.remittance_balance_usd <= 0) return Promise.resolve();
        return fetch(`/api/india-ops/${bookingId}/remittances`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
          body: JSON.stringify({ amount_usd: b.remittance_balance_usd, amount_inr: Math.round(b.remittance_balance_usd * batchRemitRate * 100) / 100, conversion_rate: batchRemitRate, remittance_date: batchRemitDate, remittance_ref: batchRemitRef, customer_pan: b.customer_pan, status: 'COMPLETED' }),
        });
      });
      await Promise.all(promises);
      alert(`Remittance recorded for ${selectedForRemit.size} bookings`);
      setShowBatchRemit(false); setSelectedForRemit(new Set()); setBatchRemitRef('');
      loadData(); loadRemittances();
    } catch (e) { alert('Error saving batch remittance'); }
    finally { setSavingBatch(false); }
  };

  if (loading) return <div className="flex justify-center items-center h-64">Loading...</div>;

  if (selectedBooking) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="secondary" size="sm" onClick={() => { setSelectedBooking(null); loadData(); loadRemittances(); }} className="text-xs">← Back</Button>
          <h1 className="text-xl font-bold">{selectedBooking.itinerary_id} — {selectedBooking.guest_name}</h1>
          <span className="text-sm text-gray-500">{selectedBooking.agency_name}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{selectedBooking.gst_invoice_number}</span>
        </div>
        <IndiaOpsTab bookingId={selectedBooking.booking_id} onRefresh={() => loadData()} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">🇮🇳 India Operations</h1>
        <p className="text-sm text-gray-600">Manage GST collections, FlyRemit remittances, and GST liability</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <p className="text-xs text-gray-500">Pending Collections</p>
          <p className="text-lg font-bold text-orange-600">₹{totals.totalCollectionPending.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <p className="text-xs text-gray-500">Pending Remittance</p>
          <p className="text-lg font-bold text-blue-600">${totals.totalRemittancePending.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <p className="text-xs text-gray-500">GST Outstanding</p>
          <p className={`text-lg font-bold ${totals.totalGstOutstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>₹{totals.totalGstOutstanding.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3">
          <p className="text-xs text-gray-500">Handling Fees (India Revenue)</p>
          <p className="text-lg font-bold text-green-600">₹{totals.totalHandlingFees.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        <Button variant={viewMode === 'bookings' ? 'primary' : 'secondary'} size="sm" onClick={() => setViewMode('bookings')} className="text-xs">📋 GST Bookings</Button>
        <Button variant={viewMode === 'remittances' ? 'primary' : 'secondary'} size="sm" onClick={() => { setViewMode('remittances'); loadRemittances(); }} className="text-xs">🔄 FlyRemit History</Button>
      </div>

      {viewMode === 'remittances' ? (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-lg shadow-sm border p-3">
              <p className="text-xs text-gray-500">Total Remitted (USD)</p>
              <p className="text-lg font-bold text-blue-600">${remittanceTotals.total_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-3">
              <p className="text-xs text-gray-500">Total Remitted (INR)</p>
              <p className="text-lg font-bold text-gray-700">₹{remittanceTotals.total_inr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-3">
              <p className="text-xs text-gray-500">Total Transfers</p>
              <p className="text-lg font-bold text-gray-700">{remittanceTotals.count}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">USD</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">INR</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">FlyRemit Ref</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">PAN</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {remittances.map((r: any) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm">{r.remittance_date ? new Date(r.remittance_date).toLocaleDateString('en-GB') : ''}</td>
                      <td className="px-3 py-3 text-sm font-medium">{r.itinerary_id || `BK-${r.booking_id}`}</td>
                      <td className="px-3 py-3 text-sm truncate max-w-[120px]">{r.guest_name || ''}</td>
                      <td className="px-3 py-3 text-sm text-right font-medium">${r.amount_usd?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-sm text-right">₹{r.amount_inr?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-500">{r.conversion_rate}</td>
                      <td className="px-3 py-3 text-sm font-mono text-xs">{r.remittance_ref || '—'}</td>
                      <td className="px-3 py-3 text-sm font-mono text-xs">{r.customer_pan || '—'}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {remittances.length === 0 && (
                <div className="text-center py-12 text-gray-500">No remittances recorded yet</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Filters + Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Travel From</label>
                <Input type="date" value={filters.travelDateFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateFrom: e.target.value })} className="text-sm w-36" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Travel To</label>
                <Input type="date" value={filters.travelDateTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateTo: e.target.value })} className="text-sm w-36" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Filter</label>
                <Select value={filters.status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })} className="text-sm w-44">
                  <option value="">All GST Bookings</option>
                  <option value="pending_collection">Pending Collection</option>
                  <option value="pending_remittance">Pending Remittance</option>
                  <option value="pending_gst">Pending GST</option>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Agency</label>
                <Input value={filters.agencyName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, agencyName: e.target.value })} placeholder="Agency name" className="text-sm w-40" />
              </div>
              <Button onClick={loadData} disabled={loading} size="sm" className="text-xs">{loading ? '...' : 'Search'}</Button>
              <div className="ml-auto">
                <Button variant={showBatchRemit ? 'primary' : 'secondary'} size="sm" onClick={() => { setShowBatchRemit(!showBatchRemit); if (!showBatchRemit) selectAllPendingRemit(); }} className="text-xs">
                  🔄 {showBatchRemit ? 'Cancel Batch' : 'Batch FlyRemit'}
                </Button>
              </div>
            </div>
          </div>

          {/* Batch Remittance Panel */}
          {showBatchRemit && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-800 mb-3">🔄 Batch FlyRemit Remittance</h3>
              <div className="text-sm mb-3">
                <span className="text-gray-600">Selected: </span><span className="font-bold">{selectedForRemit.size} bookings</span>
                <span className="mx-2">|</span>
                <span className="text-gray-600">Total USD: </span><span className="font-bold text-blue-700">${batchRemitTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                {batchRemitRate > 0 && (<><span className="mx-2">|</span><span className="text-gray-600">INR: </span><span className="font-bold">₹{batchRemitInr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></>)}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Remittance Date</label>
                  <Input type="date" value={batchRemitDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRemitDate(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">FlyRemit Reference</label>
                  <Input placeholder="FR-xxxx" value={batchRemitRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRemitRef(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">INR/USD Rate</label>
                  <Input type="number" placeholder="e.g. 84.50" value={batchRemitRate || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRemitRate(parseFloat(e.target.value) || 0)} className="text-sm" />
                </div>
                <div className="flex items-end">
                  <Button onClick={submitBatchRemittance} disabled={savingBatch || selectedForRemit.size === 0} size="sm" className="text-xs">
                    {savingBatch ? 'Saving...' : `Remit $${batchRemitTotal.toLocaleString()}`}
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAllPendingRemit} className="text-blue-600 hover:text-blue-800 underline">Select all pending</button>
                <button onClick={() => setSelectedForRemit(new Set())} className="text-gray-500 hover:text-gray-700 underline">Clear selection</button>
              </div>
            </div>
          )}

          {/* Bookings Table */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead className="bg-gray-50">
                  <tr>
                    {showBatchRemit && (
                      <th className="px-2 py-3 w-8">
                        <input type="checkbox" checked={selectedForRemit.size === bookings.filter(b => b.remittance_balance_usd > 0).length && bookings.filter(b => b.remittance_balance_usd > 0).length > 0} onChange={(e) => e.target.checked ? selectAllPendingRemit() : setSelectedForRemit(new Set())} />
                      </th>
                    )}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest / Agency</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Invoice</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collection (INR)</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remittance (USD)</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handling</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">GST</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">PAN</th>
                    <th className="px-3 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className={`hover:bg-gray-50 ${showBatchRemit && selectedForRemit.has(b.booking_id) ? 'bg-blue-50' : ''}`}>
                      {showBatchRemit && (
                        <td className="px-2 py-3">
                          <input type="checkbox" checked={selectedForRemit.has(b.booking_id)} onChange={() => toggleRemitSelection(b.booking_id)} disabled={b.remittance_balance_usd <= 0} />
                        </td>
                      )}
                      <td className="px-3 py-3 text-sm">
                        <div className="font-medium">{b.itinerary_id}</div>
                        <div className="text-xs text-gray-400">{b.travel_date ? new Date(b.travel_date).toLocaleDateString('en-GB') : ''}</div>
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <div className="truncate max-w-[120px]" title={b.guest_name}>{b.guest_name}</div>
                        <div className="text-xs text-gray-400 truncate max-w-[120px]">{b.agency_name}</div>
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <div className="font-mono text-xs">{b.gst_invoice_number}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${b.gst_status === 'DECLARED' ? 'bg-green-100 text-green-700' : b.gst_status === 'ISSUED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{b.gst_status || 'DRAFT'}</span>
                      </td>
                      <td className="px-3 py-3 text-sm text-right">
                        <div>₹{b.collected_inr.toLocaleString('en-IN')}</div>
                        <div className="text-xs text-gray-400">/ ₹{b.total_gst_invoice_inr.toLocaleString('en-IN')}</div>
                        {b.collection_balance_inr > 0
                          ? <span className="text-[10px] text-red-600 font-medium">₹{b.collection_balance_inr.toLocaleString('en-IN')} due</span>
                          : <span className="text-[10px] text-green-600">✅</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-right">
                        <div>${b.remitted_usd.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">/ ${b.package_cost_usd.toLocaleString()}</div>
                        {b.remittance_balance_usd > 0
                          ? <span className="text-[10px] text-orange-600 font-medium">${b.remittance_balance_usd.toLocaleString()} pending</span>
                          : <span className="text-[10px] text-green-600">✅</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-green-700">₹{b.handling_fee_inr.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-right">
                        {b.gst_outstanding > 0
                          ? <span className="text-red-600 font-medium">₹{b.gst_outstanding.toLocaleString('en-IN')}</span>
                          : <span className="text-green-600 text-xs">✅</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {b.customer_pan
                          ? <span className="text-xs font-mono text-gray-700">{b.customer_pan}</span>
                          : <span className="text-[10px] text-red-400">Missing</span>}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSelectedBooking(b)} className="text-xs text-blue-600 hover:text-blue-800 underline">Manage</button>
                          <button
                            onClick={async () => {
                              try {
                                const token = getToken();
                                await fetch(`/api/india-ops/${b.booking_id}/intercompany-invoice`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                  body: JSON.stringify({ amount_usd: b.package_cost_usd, description: 'Tour package services', guest_name: b.guest_name, customer_pan: b.customer_pan, status: 'ISSUED' }),
                                });
                                const res = await fetch(`/api/india-ops/${b.booking_id}/intercompany-invoice/print`, { headers: { 'Authorization': `Bearer ${token}` } });
                                if (res.ok) {
                                  const blob = await res.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `IC_Invoice_${b.itinerary_id}.docx`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else { alert('Error generating invoice'); }
                              } catch (e) { alert('Error generating intercompany invoice'); }
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700" title="Print IIW → Wynder India Invoice"
                          >🖨️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500">No GST bookings found</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndiaOpsPage;
